import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';

import { Store, select } from '@ngrx/store';
import { Observable, Subject, combineLatest, forkJoin } from 'rxjs';
import { filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';

import { TPoint, TPointsByWorkspace } from 'src/app/project/modules/points/points.model';
import { TUser } from 'src/app/project/modules/user/user.model';

import { SitePointFilterService } from 'src/app/project/modules/filters/site-point-filter.service';
import { PreferencesService } from 'src/app/project/modules/preferences/preferences-service/preferences.service';
import { UserService } from 'src/app/project/modules/user/user.service';
import { GET_USER } from 'src/app/project/modules/user/user.store';
import { UsersService } from 'src/app/project/modules/users/users.service';
import { WorkspaceService } from '../../workspace/workspace.service';
import {
  SiteTableColumnsDataService,
  TColumnsData,
} from '../site-table/site-table-columns-data.service';
import { SiteTableEventsService } from '../site-table/site-table-events.service';

import { WindowService } from '@core/services';
import { TAllUsers } from '@project/view-models';
import { DropdownService } from 'src/app/project/components/dropdown/dropdown-service/dropdown.service';
import { EWorkspaces } from 'src/app/project/modules/workspace/workspaces.enum';
import { ScrollHandlingService } from 'src/app/project/services/infinite-scrolling/scroll-handling.service';
import { EStore } from 'src/app/project/shared/enums/store.enum';
import { TActive } from '../../../services/active/active.model';
import { ActiveService } from '../../../services/active/active.service';
import { AccountService } from '../../account/account-service/account.service';
import { CombineCustomFieldsService } from '../../custom-fields/combine-custom-fields/combine-custom-fields.service';
import { TCustomField } from '../../custom-fields/custom-fields.model';
import { QuickFilterService } from '../../filters/filters-api/search/quick-filter-service';
import { startPointModalOpenTimer } from '../../points/point-full-modal/point-modal-open-monitor';
import { buildSlowModalOpenReport } from '../../points/point-full-modal/utils/build-slow-modal-open-report';
import { PointAttachmentsService } from '../../points/point-modal/point-attachments/point-attachments.service';
import { PointSearchService } from '../../points/point-search/point-search.service';
import { SavedViewsService } from '../../saved-views/saved-views.service';
import { SiteOptionsService } from '../site-options/site-options.service';
import { TableColumnsService } from '../site-table/columns/table-columns.service';
import { CustomTableService } from '../site-table/custom-table/custom-table.service';
import { TableLoadingService } from '../site-table/custom-table/table-loading.service';
import Table from '../site-table/custom-table/table/Table';
import { CLEAR_COLLAPSED_GROUPS } from '../site-table/table.ui.store';
import { SiteTableOverviewService } from './site-table-overview.service';

@Component({
  selector: 'pp-site-table-overview',
  templateUrl: './site-table-overview.component.html',
  styleUrls: ['../site-table/site-table.component.scss'],
  standalone: false,
})
export class SiteTableOverviewComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('tableWrapper', { static: true }) tableWrapperElement: ElementRef;
  @ViewChild('table', { static: true }) tableElement: ElementRef;

  @Output() ppTableCreated = new EventEmitter();

  private readonly destroy$ = new Subject<void>();

  columns: TColumnsData = this.siteTableColumnsDataService.getAllColumns();

  points$: Observable<TPointsByWorkspace>;
  points: TPoint[] = [];
  private active$: Observable<TActive>;
  private table: Table;
  isTableLoading: Observable<boolean>;
  public users: TAllUsers;
  public user: TUser;

  constructor(
    private store: Store<{ points: TPointsByWorkspace; active: TActive }>,
    private siteTableEventsService: SiteTableEventsService,
    private workspaceService: WorkspaceService,
    private siteTableColumnsDataService: SiteTableColumnsDataService,
    private usersService: UsersService,
    private userService: UserService,
    private sitePointFilterService: SitePointFilterService,
    private preferencesService: PreferencesService,
    private router: Router,
    private pointAttachmentsService: PointAttachmentsService,
    private activeService: ActiveService,
    private siteOptionsService: SiteOptionsService,
    private siteTableOverviewService: SiteTableOverviewService,
    private dropdownService: DropdownService,
    private customTableService: CustomTableService,
    private tableColumnsService: TableColumnsService,
    private savedViewsService: SavedViewsService,
    private pointSearchService: PointSearchService,
    private scrollHandlingService: ScrollHandlingService,
    private tableLoadingService: TableLoadingService,
    private quickFilterService: QuickFilterService,
    private combineCustomFieldsService: CombineCustomFieldsService,
    private windowService: WindowService,
  ) {
    this.points$ = this.store.pipe(select(EStore.POINTS));
    this.active$ = this.store.pipe(select(EStore.ACTIVE));
    this.isTableLoading = tableLoadingService.isTableLoading$;
  }

  ngOnInit() {
    this.users = this.usersService.getUsers();
    this.user = this.userService.getUser();

    this.points$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.points = [];

      this.users = this.usersService.getUsers();
    });

    combineLatest([this.active$, this.tableLoadingService.isTableLoading$])
      .pipe(
        takeUntil(this.destroy$),
        filter(([, loading]) => !loading),
      )
      .subscribe(([active]) => {
        if (this.table) {
          this.table.setActivePointId(active._id);
        }
      });
  }

  ngAfterViewInit() {
    this.quickFilterService.resetFilters();

    this.userService
      .fetchUser()
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => {
          this.table = this.createTableInstance();

          this.tableWrapperElement.nativeElement.replaceChild(
            this.table.element,
            this.tableElement.nativeElement,
          );

          this.table.updateColumns();
          this.table.updateWidth();
          this.table.updatePoints(this.points);
          this.table.load({ keepScrollPosition: false });
          this.sitePointFilterService.filterPoints();

          const fetchWorkspaces$ = this.workspaceService.fetchWorkspaces().pipe(
            takeUntil(this.destroy$),
            tap((accounts) => {
              const customFields = AccountService.getAccountsWorkspacesCustomFields(accounts);

              this.customTableService.checkColumns(customFields, EWorkspaces.OVERVIEW);
              this.table.updateVisibleColumnIndexes();
              this.table.updateVisibleColumnPositions();
              this.table.load({ keepScrollPosition: false });

              this.processPreferencesResponse();
            }),
          );

          const fetchUsers$ = this.usersService.fetchAllUsers({ refetch: true }).pipe(
            takeUntil(this.destroy$),
            tap((response) => {
              this.table.loadAvatars(response);
            }),
          );

          return forkJoin([fetchWorkspaces$, fetchUsers$]).pipe(
            tap(() => {
              this.ppTableCreated.emit();
            }),
          );
        }),
      )
      .subscribe();
  }

  ngOnDestroy() {
    this.savedViewsService.setSelectedViewId(EWorkspaces.OVERVIEW, null);
    this.destroy$.next();
    this.table?.destroy();
    this.table = null;
  }

  openPoint(point: TPoint): void {
    const pointSwitchingBlocked = this.pointAttachmentsService.blocksPointSwitching();

    if (!pointSwitchingBlocked) {
      startPointModalOpenTimer(() =>
        buildSlowModalOpenReport({
          url: window.location.href,
          overview: true,
          workspaceId: point.workspaceRef?.id,
          pointId: point._id,
          user: GET_USER(),
        }),
      );

      this.siteTableEventsService.openPoint({ _id: point._id }, { isOverview: true });
    }
  }

  processPreferencesResponse(): void {
    const savedViews = this.savedViewsService.getSavedViews(EWorkspaces.OVERVIEW);
    const selectedViewId = this.savedViewsService.getSelectedViewId(EWorkspaces.OVERVIEW);

    const currentWorkspacePrefs = this.preferencesService.getCurrentWorkspaceLayout({
      savedViews,
      selectedViewId,
    });

    this.table = this.customTableService.getTable();
    this.siteTableOverviewService.processOverviewLayout(
      currentWorkspacePrefs,
      this.tableColumnsService.getColumns(),
    );

    this.sitePointFilterService.filterPointsFromSavedView({
      _keepScrollPosition: false,
    });
  }

  isOverviewPage(): boolean {
    return this.router.url === '/site/overview';
  }

  private createTableInstance(): Table {
    CLEAR_COLLAPSED_GROUPS();

    return new Table(true, {
      workspaceId: null,
      dropdownService: this.dropdownService,
      windowService: this.windowService,
      selectPointCallback: (_point): void => {
        this.siteTableEventsService.togglePointSelection({
          workspaceId: _point.workspaceRef.id,
          _id: _point._id,
        });
      },
      selectAllPointsCallback: (selectAll: boolean): void => {
        this.siteTableEventsService.toggleAllPointsSelection(selectAll);
      },
      openPointCallback: (_point): void => {
        this.openPoint(_point);
      },
      savePreferencesCallback: (): void => {
        this.preferencesService.savePreferences(EWorkspaces.OVERVIEW);
      },
      getCustomFieldsCallback: (): Observable<TCustomField[]> =>
        this.workspaceService.fetchWorkspaces().pipe(
          takeUntil(this.destroy$),
          map((accounts) => AccountService.getAccountsWorkspacesCustomFields(accounts)),
        ),
      pointAttachmentsService: this.pointAttachmentsService,
      activePointId: this.activeService.getActivePointId(),
      siteOptionsService: this.siteOptionsService,
      customTableService: this.customTableService,
      pointSearchService: this.pointSearchService,
      scrollHandlingService: this.scrollHandlingService,
      tableLoadingService: this.tableLoadingService,
      combineCustomFieldsService: this.combineCustomFieldsService,
    });
  }
}
